/**
 * Sales Projection Detail Dialog
 *
 * Provides a granular breakdown of how sales projections are calculated
 * for a specific inventory item, with the ability to set manual overrides
 * for specific periods.
 *
 * Features:
 * - Projection basis summary showing calculation method and source data
 * - Future periods table with base projections, modifiers, and override inputs
 * - Manual override entry for each period
 *
 * Props:
 * - inventoryGrouping: The inventory item to display details for (null when closed)
 * - runRateBasisMode: "rolling" or "fixed" mode
 * - runRateBasis: Number of days for rolling mode
 * - fixedRunRateStart: Start date for fixed mode
 * - fixedRunRateEnd: End date for fixed mode
 * - salesModifiers: Global and per-inventory sales modifiers
 * - manualOverrides: Manual override values by inventory and period
 * - onUpdateManualOverride: Callback to update a manual override
 * - onClose: Callback when dialog closes
 * - onSave: Callback to save changes
 */

import React, { useMemo } from "react";
import moment from "moment";
import {
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { InventoryGrouping } from "models/InventoryModel";
import { SalesModifiersData, ManualOverrides, RunRateBasisMode } from "../../types";
import { calculateProjectionBreakdown, formatNumber } from "../../utils";
import ComparisonSalesRange from "./ComparisonSalesRange";
import { WHAT_IF_STATUS, WhatIfSimulation } from "../../demandWhatIf";

const formatForecastDate = (date: string | null | undefined): string => (date ? moment(date).format("MMM D, YYYY") : "none in range");

interface SalesProjectionDetailDialogProps {
  inventoryGrouping: InventoryGrouping | null;
  runRateBasisMode: RunRateBasisMode;
  runRateBasis: number;
  fixedRunRateStart: string | null;
  fixedRunRateEnd: string | null;
  salesModifiers: SalesModifiersData;
  manualOverrides: ManualOverrides;
  serverForecast?: any;
  onUpdateManualOverride: (inventoryId: string, periodKey: string, value: number | null) => void;
  onUpdateInventoryModifier: (inventoryId: string, monthKey: string, value: number | null) => void;
  onClose: () => void;
  /** Opens the "visible to everyone" confirmation for this product's unsaved adjustments */
  onSave: () => void;
  /** This product row's what-if, while it has unsaved adjustments or a trial daily rate */
  whatIf?: WhatIfSimulation;
  /** Whether the product has unsaved month adjustments (the part of a what-if that can be saved) */
  hasUnsavedAdjustments: boolean;
  trialUnitsPerDay?: number;
  onTrialUnitsPerDayChange: (unitsPerDay: number | null) => void;
  onDiscardWhatIf: () => void;
  /** The product's daily sales series is still loading (fetched when the pop-up opens) */
  isLoadingSalesSeries?: boolean;
  /** Why the series could not be loaded; the breakdown needs it */
  salesSeriesError?: string | null;
}

export default function SalesProjectionDetailDialog({
  inventoryGrouping,
  runRateBasisMode,
  serverForecast,
  runRateBasis,
  fixedRunRateStart,
  fixedRunRateEnd,
  salesModifiers,
  manualOverrides,
  onUpdateManualOverride,
  onUpdateInventoryModifier,
  onClose,
  onSave,
  whatIf,
  hasUnsavedAdjustments,
  trialUnitsPerDay,
  onTrialUnitsPerDayChange,
  onDiscardWhatIf,
  isLoadingSalesSeries = false,
  salesSeriesError = null,
}: SalesProjectionDetailDialogProps): JSX.Element {
  // Calculate projection breakdown
  const projectionBreakdown = useMemo(() => {
    if (!inventoryGrouping) return null;
    return calculateProjectionBreakdown(
      inventoryGrouping,
      runRateBasisMode,
      runRateBasis,
      fixedRunRateStart,
      fixedRunRateEnd,
    );
  }, [inventoryGrouping, runRateBasisMode, runRateBasis, fixedRunRateStart, fixedRunRateEnd]);

  // Generate future periods (next 6 months)
  const futurePeriods = useMemo(() => {
    const periods: Array<{
      key: string;
      label: string;
      daysInPeriod: number;
      startDate: string;
      endDate: string;
    }> = [];

    const today = moment();
    const currentMonth = today.clone().startOf("month");

    for (let i = 0; i < 12; i++) {
      const periodStart = currentMonth.clone().add(i, "months");
      const periodEnd = periodStart.clone().endOf("month");
      const key = periodStart.format("YYYY-MM");

      // For current month, only count remaining days
      let daysInPeriod: number;
      let effectiveStartDate: string;

      if (i === 0) {
        // Current month: from today to end of month
        daysInPeriod = periodEnd.diff(today, "days") + 1;
        effectiveStartDate = today.format("YYYY-MM-DD");
      } else {
        daysInPeriod = periodEnd.diff(periodStart, "days") + 1;
        effectiveStartDate = periodStart.format("YYYY-MM-DD");
      }

      periods.push({
        key,
        label: periodStart.format("MMMM YYYY"),
        daysInPeriod,
        startDate: effectiveStartDate,
        endDate: periodEnd.format("YYYY-MM-DD"),
      });
    }

    return periods;
  }, []);

  // Build server sold lookup from aggregated ledger (source of truth)
  const serverSoldByMonth = useMemo(() => {
    const map = new Map<string, number>();
    if (!serverForecast?.aggregatedLedger) return map;
    for (const period of serverForecast.aggregatedLedger) {
      if (period.isProjected) {
        map.set(period.key, Math.round(period.sold));
      }
    }
    return map;
  }, [serverForecast]);

  // Get effective modifier for a period
  const getEffectiveModifier = (periodKey: string): number | undefined => {
    if (!inventoryGrouping) return undefined;

    const inventoryModifier = salesModifiers.byInventory[inventoryGrouping.inventory_id]?.[periodKey];
    if (inventoryModifier !== undefined) return inventoryModifier;

    return salesModifiers.global[periodKey];
  };

  // Get manual override for a period
  const getManualOverride = (periodKey: string): number | undefined => {
    if (!inventoryGrouping) return undefined;
    return manualOverrides[inventoryGrouping.inventory_id]?.[periodKey];
  };

  // Calculate base projection for a period
  const getBaseProjection = (daysInPeriod: number): number => {
    if (!projectionBreakdown) return 0;
    return projectionBreakdown.dailyAverage * daysInPeriod;
  };

  // Calculate projected value.
  //   periodIdx 0 (current month): show only the projected-remainder portion
  //     locally. The server aggregate for the current month would mix past
  //     actual sales with the projected days, which is misleading in a
  //     forecast-verification view.
  //   periodIdx >= 1: use server aggregate (which is fully projected).
  const getProjectedValue = (baseProjection: number, periodKey: string, periodIdx: number): number => {
    const override = getManualOverride(periodKey);
    if (override !== undefined) return override;

    const modifier = getEffectiveModifier(periodKey) ?? 1.0;

    if (periodIdx === 0) {
      return baseProjection * modifier;
    }

    const serverSold = serverSoldByMonth.get(periodKey);
    if (serverSold != null) return serverSold;

    return baseProjection * modifier;
  };

  const handleOverrideChange = (periodKey: string, value: string) => {
    if (!inventoryGrouping) return;

    if (value === "") {
      onUpdateManualOverride(inventoryGrouping.inventory_id, periodKey, null);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0) {
        onUpdateManualOverride(inventoryGrouping.inventory_id, periodKey, numValue);
      }
    }
  };

  const handleModifierChange = (periodKey: string, value: string) => {
    if (!inventoryGrouping) return;

    if (value === "") {
      onUpdateInventoryModifier(inventoryGrouping.inventory_id, periodKey, null);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0) {
        onUpdateInventoryModifier(inventoryGrouping.inventory_id, periodKey, numValue);
      }
    }
  };

  return (
    <Dialog
      open={inventoryGrouping !== null}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Sales Projection Details
        {inventoryGrouping && (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {inventoryGrouping.inventory_name || inventoryGrouping.inventory_id}
            {inventoryGrouping.inventory_name && (
              <span style={{ marginLeft: 8, fontSize: "0.85em" }}>
                ({inventoryGrouping.inventory_id})
              </span>
            )}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto" }}>
        {isLoadingSalesSeries && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 4, justifyContent: "center" }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Loading this product's sales…</Typography>
          </Box>
        )}
        {!isLoadingSalesSeries && salesSeriesError && (
          <Alert severity="error">Could not load this product's sales: {salesSeriesError}. Close and open it again to retry.</Alert>
        )}
        {!isLoadingSalesSeries && !salesSeriesError && inventoryGrouping && projectionBreakdown && (
          <>
            {/* Projection Basis Summary Card */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: "rgb(250, 250, 255)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Projection Basis
                </Typography>
                <Chip
                  label={runRateBasisMode === "rolling" ? "Rolling Window" : "Fixed Date Range"}
                  size="small"
                  color={runRateBasisMode === "rolling" ? "primary" : "secondary"}
                  variant="outlined"
                />
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
                    Date Range
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {moment(projectionBreakdown.dateRangeStart).format("MMM D")} -{" "}
                    {moment(projectionBreakdown.dateRangeEnd).format("MMM D, YYYY")}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
                    Days in Range
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {projectionBreakdown.daysInRange}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
                    Total Sales
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatNumber(Math.round(projectionBreakdown.totalSales))}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
                    Daily Average
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {projectionBreakdown.dailyAverage.toFixed(1)} units/day
                  </Typography>
                </Box>
              </Box>

              {/* Server-computed projection (what /forecasts actually used).
                  When fixed mode is on, the server basis should be "<start> to <end>"
                  and the server rate should match the local Daily Average within rounding. */}
              {serverForecast?.projection && (() => {
                const serverBasis: string = serverForecast.projection.basis;
                const serverRate: number = Number(serverForecast.projection.unitsPerDay);
                const localRate = projectionBreakdown.dailyAverage;
                const expectedBasis = runRateBasisMode === "fixed" && fixedRunRateStart && fixedRunRateEnd
                  ? `${fixedRunRateStart} to ${fixedRunRateEnd}`
                  : `L${runRateBasis}`;
                const basisMatches = serverBasis === expectedBasis;
                const rateMatches = Math.abs(serverRate - localRate) < 0.5;
                const allMatch = basisMatches && rateMatches;

                return (
                  <Box
                    sx={{
                      mt: 1,
                      pt: 1.5,
                      borderTop: "1px dashed rgba(0,0,0,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Server projection used for table:
                      </Typography>
                      <Tooltip
                        title={
                          allMatch
                            ? `Forecasts endpoint used basis "${serverBasis}" at ${serverRate.toFixed(2)} units/day — matches the date range above.`
                            : `Forecasts endpoint used basis "${serverBasis}" at ${serverRate.toFixed(2)} units/day. Expected "${expectedBasis}" at ~${localRate.toFixed(2)} units/day. Mismatch suggests the fixed range isn't being applied server-side.`
                        }
                        arrow
                        placement="top"
                      >
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 600,
                            color: allMatch ? "rgb(16, 122, 87)" : "rgb(180, 64, 0)",
                            borderBottom: "1px dotted rgba(0,0,0,0.3)",
                            cursor: "help",
                          }}
                        >
                          {serverBasis} @ {serverRate.toFixed(1)}/day
                        </Box>
                      </Tooltip>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: allMatch ? "rgb(16, 122, 87)" : "rgb(180, 64, 0)",
                        }}
                      >
                        {allMatch ? "✓ matches local" : "⚠ differs from local"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
            </Paper>

            <ComparisonSalesRange
              inventoryId={inventoryGrouping.inventory_id}
              country={inventoryGrouping.country}
              basisStart={projectionBreakdown.dateRangeStart}
              basisEnd={projectionBreakdown.dateRangeEnd}
              basisDailyAverage={projectionBreakdown.dailyAverage}
              trialUnitsPerDay={trialUnitsPerDay}
              onTrialUnitsPerDayChange={onTrialUnitsPerDayChange}
            />

            {whatIf && (
              <Alert
                severity={whatIf.status === WHAT_IF_STATUS.ERROR ? "error" : "info"}
                icon={whatIf.status === WHAT_IF_STATUS.LOADING ? <CircularProgress size={18} /> : undefined}
                sx={{ mb: 3 }}
              >
                <strong>What-if — not saved, only you can see it.</strong>{" "}
                {whatIf.status === WHAT_IF_STATUS.ERROR && <>Could not recalculate: {whatIf.error}</>}
                {whatIf.status === WHAT_IF_STATUS.LOADING && !whatIf.result && <>Recalculating this product…</>}
                {whatIf.result && (
                  <Box sx={{ mt: 0.5, opacity: whatIf.status === WHAT_IF_STATUS.LOADING ? 0.5 : 1 }}>
                    <div>Out of stock: {formatForecastDate(whatIf.result.baseline.stockOutDate)} → <strong>{formatForecastDate(whatIf.result.simulated.stockOutDate)}</strong></div>
                    <div>Recommended order date: {formatForecastDate(whatIf.result.baseline.recommendedOrderDate)} → <strong>{formatForecastDate(whatIf.result.simulated.recommendedOrderDate)}</strong></div>
                    <div>Projected sales over the risk window: {formatNumber(Math.round(whatIf.result.baseline.projectedSalesInPeriod))} → <strong>{formatNumber(Math.round(whatIf.result.simulated.projectedSalesInPeriod))}</strong> units</div>
                    <div>Risk: {whatIf.result.baseline.severity} → <strong>{whatIf.result.simulated.severity}</strong></div>
                  </Box>
                )}
              </Alert>
            )}

            {/* Future Periods Table */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Future Periods
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "rgb(245, 245, 245)" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Days</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Base Projection</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Modifier</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Projected Value</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: 120 }}>
                      Manual Override
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {futurePeriods.map((period, periodIdx) => {
                    const baseProjection = getBaseProjection(period.daysInPeriod);
                    const effectiveModifier = getEffectiveModifier(period.key);
                    const manualOverride = getManualOverride(period.key);
                    const projectedValue = getProjectedValue(baseProjection, period.key, periodIdx);

                    // Tooltip mirrors the server's projection algorithm:
                    //   periodIdx 0,1: dailyAverage * daysInPeriod * modifier
                    //   periodIdx >= 2: prev month server total * modifier
                    let breakdownTooltip: string;
                    if (manualOverride !== undefined) {
                      breakdownTooltip = `Manual Override: ${formatNumber(Math.round(manualOverride))} units`;
                    } else if (periodIdx <= 1) {
                      const dailyAvg = projectionBreakdown!.dailyAverage;
                      const modVal = effectiveModifier ?? 1.0;
                      const modStr = modVal !== 1 ? ` × ${modVal}x` : "";
                      breakdownTooltip = `${dailyAvg.toFixed(1)} units/day × ${period.daysInPeriod} days${modStr} = ${formatNumber(Math.round(projectedValue))}`;
                    } else {
                      const prevKey = futurePeriods[periodIdx - 1].key;
                      const prevSold = serverSoldByMonth.get(prevKey);
                      const modVal = effectiveModifier ?? 1.0;
                      if (prevSold != null) {
                        breakdownTooltip = `Prev month (${formatNumber(prevSold)}) × ${modVal}x = ${formatNumber(Math.round(projectedValue))}`;
                      } else {
                        breakdownTooltip = `${formatNumber(Math.round(projectedValue))} projected`;
                      }
                    }

                    // Base projection tooltip (local calculation for reference)
                    const baseTooltip = `${projectionBreakdown!.dailyAverage.toFixed(1)} units/day × ${period.daysInPeriod} days (local estimate)`;

                    // Determine if modifier is global vs inventory-specific
                    const invModifier = salesModifiers.byInventory[inventoryGrouping.inventory_id]?.[period.key];
                    const isGlobalModifier = invModifier === undefined && effectiveModifier !== undefined;

                    return (
                      <TableRow
                        key={period.key}
                        sx={{
                          backgroundColor: manualOverride !== undefined
                            ? "rgba(124, 58, 237, 0.06)"
                            : undefined,
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {period.label}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.6 }}>
                            {period.startDate} → {period.endDate}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {period.daysInPeriod}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={baseTooltip} arrow placement="top">
                            <Box component="span" sx={{ borderBottom: "1px dotted rgba(0,0,0,0.3)", cursor: "help" }}>
                              {formatNumber(Math.round(baseProjection))}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={isGlobalModifier ? "Global modifier (edit to override for this item)" : ""} arrow>
                            <TextField
                              size="small"
                              type="number"
                              value={effectiveModifier ?? ""}
                              placeholder="1.0"
                              onChange={(e) => handleModifierChange(period.key, e.target.value)}
                              inputProps={{
                                step: 0.1,
                                min: 0,
                                style: {
                                  textAlign: "center",
                                  padding: "6px 8px",
                                  fontSize: "0.875rem",
                                  fontStyle: isGlobalModifier ? "italic" : "normal",
                                },
                              }}
                              sx={{
                                width: 80,
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: (effectiveModifier !== undefined && effectiveModifier !== 1)
                                    ? isGlobalModifier ? "rgb(240, 240, 255)" : "rgb(255, 255, 230)"
                                    : "white",
                                },
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: manualOverride !== undefined ? 600 : 500,
                          color: manualOverride !== undefined ? '#7C3AED' : 'inherit',
                        }}>
                          <Tooltip title={breakdownTooltip} arrow placement="top">
                            <Box component="span" sx={{ borderBottom: "1px dotted rgba(0,0,0,0.3)", cursor: "help" }}>
                              {formatNumber(Math.round(projectedValue))}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            size="small"
                            type="number"
                            value={manualOverride ?? ""}
                            placeholder="—"
                            onChange={(e) => handleOverrideChange(period.key, e.target.value)}
                            inputProps={{
                              min: 0,
                              style: {
                                textAlign: "center",
                                padding: "6px 8px",
                                fontSize: "0.875rem",
                                color: manualOverride !== undefined ? '#7C3AED' : 'inherit',
                                fontWeight: manualOverride !== undefined ? 600 : 'normal',
                              },
                            }}
                            sx={{
                              width: 100,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: manualOverride !== undefined
                                  ? "rgba(124, 58, 237, 0.08)"
                                  : "white",
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.6 }}>
              Manual overrides replace the calculated projection for that period. Leave blank to use the calculated value.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {whatIf && (
          <Button color="inherit" onClick={onDiscardWhatIf} sx={{ mr: "auto" }}>
            Discard what-if
          </Button>
        )}
        <Tooltip title={whatIf ? "The what-if stays on the table, marked as not saved, until you discard or save it" : ""} arrow>
          <Button onClick={onClose}>Close</Button>
        </Tooltip>
        <Tooltip title={hasUnsavedAdjustments ? "" : "Nothing to save: change a month's modifier or manual override first"} arrow>
          <span>
            <Button variant="contained" onClick={onSave} disabled={!hasUnsavedAdjustments}>
              Save for everyone…
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
